import { Fragment, useState, useEffect } from "react";
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonGrid,
  IonRow,
  IonCol,
  IonFooter,
  useIonAlert,
  IonItem,
  IonLabel,
  IonInput,
  IonAvatar,
  IonToggle,
} from "@ionic/react";
import { useForm } from "react-hook-form";
import { PageError, displayMsg, Spinner } from "pymble-ui/components";
import { close } from "ionicons/icons";
import { useSelectIconPopover } from "../hooks/useSelectIconPopover";
import { useGroupsBulk } from "../hooks/useGroupsBulk";
import { formatISO, parseISO } from "date-fns";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";

import "./BulkGroups.css";

const parseGroupDate = (dateString: string | null) => {
  return dateString ? formatISO(parseISO(dateString)) : null;
};

const formStyles = {
  textField: { width: 140 },
} as const;

type FormValues = {
  icon: string;
  start_at: string;
  end_at: string;
  show_member_list: boolean;
};

// Map form field name to Document field names
const FIELD_MAP: Record<string, string> = {
  icon: "Icon",
  start_at: "StartAt",
  end_at: "EndAt",
  show_member_list: "ShowMemberList",
};

interface BulkGroupsProps {
  onDismiss: () => void;
  onUpdateComplete: () => void;
  selectedGroups: (string | number)[];
}

interface BulkResult {
  status: string;
  modified?: number;
}

const BulkGroups: React.FC<BulkGroupsProps> = ({
  onDismiss,
  onUpdateComplete,
  selectedGroups,
}) => {
  const classes = formStyles;
  const [allowAction, setAllowAction] = useState<boolean>(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  const icon = "https://pymblelc.blob.core.windows.net/assets/pymble-p.png";
  const start_at = "";
  const end_at = "";
  const show_member_list = false;

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      icon,
      start_at,
      end_at,
      show_member_list,
    },
  });

  const { presentIcons, selectedIcon, IconsPopover } = useSelectIconPopover();

  useEffect(() => {
    reset({
      icon,
      start_at,
      end_at,
      show_member_list,
    });
  }, [reset, icon, start_at, end_at, show_member_list]);

  useEffect(() => {
    setValue("icon", selectedIcon.img ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setValue is stable from react-hook-form
  }, [selectedIcon]);

  useEffect(() => {
    setAllowAction(selectedFields.length > 0);
  }, [selectedFields]);

  const onSubmit = async (data: FormValues) => {
    console.log("onSubmit:", data);
    confirmBulkUpdate(data);
  };

  const [confirmAction] = useIonAlert();

  const { updateGroups } = useGroupsBulk();

  const confirmBulkUpdate = (data: FormValues) => {
    confirmAction({
      cssClass: "bulk-action-alert",
      header: "Confirm",
      message: `Update ${selectedGroups.length} group${
        selectedGroups.length > 1 ? "s" : ""
      } with the selected value${selectedFields.length > 1 ? "s" : ""}?`,
      buttons: [
        "Cancel",
        {
          text: "Go",
          handler: () => handleUpdateGroups(data),
        },
      ],
      onDidDismiss: () => console.log("Add cancelled"),
    });
  };

  const handleUpdateGroups = async (data: FormValues) => {
    setShowSpinner(true);
    const groups = selectedGroups as string[];

    const update: Record<string, unknown> = {};

    try {
      // Build our "update" document for the update
      for (const f of Object.keys(data)) {
        if (isFieldSelected(f)) {
          if (f === "start_at" || f === "end_at") {
            // Dates need to be parsed and formatted correctly
            update[FIELD_MAP[f]] = parseGroupDate(data[f as keyof FormValues] as string);
          } else {
            update[FIELD_MAP[f]] = data[f as keyof FormValues];
          }
        }
      }

      // Send the update request
      const result = await updateGroups(groups, update) as BulkResult;

      if (result.status !== "ok") {
        await displayMsg(
          "Oops, could not update groups.  Please check and try again.",
          {
            type: "danger",
            position: "middle",
            duration: 5000,
          }
        );
      } else {
        onUpdateComplete();
        await displayMsg(
          `Update successful. ${result.modified} groups modified.`,
          {
            type: "success",
            position: "middle",
            duration: 3000,
          }
        );
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("Error: ", msg);
    } finally {
      setShowSpinner(false);
    }
  };

  // Set of functions to manage selected field state

  const isFieldSelected = (field: string) => {
    const isSelected = selectedFields.find((f) => f === field);
    return isSelected ? true : false;
  };

  const addField = (field: string) => {
    if (!isFieldSelected(field)) {
      const newSelection = [...selectedFields, field];
      setSelectedFields(newSelection);
    }
  };

  const removeField = (field: string) => {
    const newSelection = selectedFields.filter((f) => f !== field);
    setSelectedFields(newSelection);
  };

  const handleFieldChange = (field: string, selected: boolean) => {
    if (selected === true) {
      addField(field);
    } else {
      removeField(field);
    }
  };

  return (
    <Fragment>
      <form onSubmit={handleSubmit(onSubmit)} style={{ height: "50vh" }}>
        <IonHeader>
          <IonToolbar color="light">
            <IonTitle>Bulk Group Update</IonTitle>
            <IonButtons slot="end">
              <IonButton fill="clear" size="small" onClick={onDismiss}>
                <IonIcon slot="icon-only" icon={close} size="small" />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonGrid>
            <IonRow>
              <IonCol size="2">Enable</IonCol>
              <IonCol>Value</IonCol>
            </IonRow>
            <IonRow className="ion-align-items-center">
              <IonCol size="2">
                <IonToggle
                  checked={
                    selectedFields.find((f) => f === "icon") ? true : false
                  }
                  onIonChange={(e) =>
                    handleFieldChange("icon", e.detail.checked)
                  }
                />
              </IonCol>
              <IonCol>
                <IonItem
                  lines="none"
                  disabled={
                    selectedFields.find((f) => f === "icon") ? false : true
                  }
                >
                  <IonLabel color="primary">
                    Icon
                  </IonLabel>
                  <IonInput
                    value={icon}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    {...(register("icon", { required: false }) as any)}
                    placeholder="Icon"
                    className="ion-hide"
                  ></IonInput>
                  <IonAvatar
                    className="group-form-icon"
                    onClick={(e) =>
                      presentIcons({
                        event: e.nativeEvent,
                      })
                    }
                  >
                    <img
                      src={selectedIcon?.img ? selectedIcon.img : icon}
                      alt="icon"
                    />
                  </IonAvatar>
                  <PageError error={errors.icon?.message} />
                </IonItem>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="2">
                <IonToggle
                  checked={
                    selectedFields.find((f) => f === "start_at") ? true : false
                  }
                  onIonChange={(e) =>
                    handleFieldChange("start_at", e.detail.checked)
                  }
                />
              </IonCol>
              <IonCol>
                <IonItem
                  lines="none"
                  disabled={
                    selectedFields.find((f) => f === "start_at") ? false : true
                  }
                >
                  <TextField
                    label="Start At"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    {...register("start_at", { required: false })}
                    placeholder="Start Date"
                    sx={classes.textField}
                  />
                  <PageError error={errors.start_at?.message} />
                </IonItem>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="2">
                <IonToggle
                  checked={
                    selectedFields.find((f) => f === "end_at") ? true : false
                  }
                  onIonChange={(e) =>
                    handleFieldChange("end_at", e.detail.checked)
                  }
                />
              </IonCol>
              <IonCol>
                <IonItem
                  lines="none"
                  disabled={
                    selectedFields.find((f) => f === "end_at") ? false : true
                  }
                >
                  <TextField
                    label="End At"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    {...register("end_at", { required: false })}
                    placeholder="End Date"
                    sx={classes.textField}
                  />
                  <PageError error={errors.end_at?.message} />
                </IonItem>
              </IonCol>
            </IonRow>

            <IonRow>
              <IonCol size="2">
                <IonToggle
                  checked={
                    selectedFields.find((f) => f === "show_member_list")
                      ? true
                      : false
                  }
                  onIonChange={(e) =>
                    handleFieldChange("show_member_list", e.detail.checked)
                  }
                />
              </IonCol>
              <IonCol>
                <IonItem
                  lines="none"
                  disabled={
                    selectedFields.find((f) => f === "show_member_list")
                      ? false
                      : true
                  }
                >
                  <IonLabel color="primary">
                    Show Member List
                  </IonLabel>
                  <Switch
                    {...register("show_member_list", { required: false })}
                  />
                  <PageError error={errors.show_member_list?.message} />
                </IonItem>
              </IonCol>
            </IonRow>
          </IonGrid>
          {IconsPopover}
        </IonContent>
        <IonFooter>
          <IonToolbar>
            <IonButtons slot="end">
              {showSpinner && <Spinner />}

              <IonButton
                className="ion-text-capitalize"
                color="secondary"
                fill="solid"
                disabled={!allowAction}
                type="submit"
              >
                Bulk Update
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      </form>
    </Fragment>
  );
};

export default BulkGroups;
